/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {
    UgcBuildErrorCodeInteface,
    UgcBuildInterface, UgcBuildPresignedRequestInterface,
    UgcBuildPresignedResponseInterface,
    UgcBuildStateInterface, UgcRevisionInterface,
} from '@/store/modules/ugc-build/types';
import {UgcItemInterface} from '@/store/modules/ugc-item/types';
import {AxiosResponse} from 'axios';
import api from '@/services/api';
import {UgcNotificationItemInterface} from '@/store/modules/ugc-notification/types';

/**
 * Actions file for UGC (item) build operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<UgcBuildStateInterface, RootState> = {
    /**
     * Initialise UGC item action.
     *
     * @param {any} commit
     * @param {string} itemId
     *
     * @returns {Promise<UgcItemInterface>}
     */
    initialise({commit}: {commit: any}, itemId: string): Promise<UgcItemInterface> {
        return api.axios.get(`api/item/${itemId}`).then((response: AxiosResponse<UgcItemInterface>) => {
            response.data.price /= 100;
            response.data.runningCost /= 100;
            commit('addItem', response.data);
            return response.data;
        });
    },

    /**
     * Apply notifications to current data set.
     *
     * @param {any} commit
     * @param {UgcNotificationItemInterface[]} notifications
     */
    notifications({commit}: {commit: any}, notifications: UgcNotificationItemInterface[]): void {
        commit('applyNotifications', notifications);
    },

    /**
     * Initialise UGC item action.
     *
     * @param {any} commit
     * @param {UgcItemInterface} item
     *
     * @returns {Promise<UgcRevisionInterface[]>}
     */
    loadRevisions({commit}: {commit: any}, item: UgcItemInterface): Promise<UgcRevisionInterface[]> {
        return api.axios.get(`api/item/${item.id}/build`).then((response: AxiosResponse<UgcRevisionInterface[]>) => {
            for (const revision of response.data) {
                if (!revision.item) {
                    continue;
                }

                revision.originalItem = JSON.parse(revision.item);
                if (undefined !== revision.originalItem) {
                    revision.originalItem.price = (revision.originalItem.price || 0) / 100;
                    revision.originalItem.runningCost = (revision.originalItem.runningCost || 0) / 100;
                }
            }

            commit('addRevisionItems', response.data);
            return response.data;
        });
    },

    /**
     * Create a new UGC revision item.
     *
     * @param {any} commit
     * @param {UgcRevisionInterface} build
     */
    createRevision({commit}: {commit: any}, build: UgcBuildInterface): Promise<UgcRevisionInterface> {
        build.price = (build.price || 0) * 100;
        build.runningCost = (build.runningCost || 0) * 100;
        return api.axios.post('api/item/build', JSON.stringify(build))
            .then((response: AxiosResponse<UgcRevisionInterface>) => {
                commit('addRevisionItem', response.data);
                return response.data;
        });
    },

    /**
     * Delete a new UGC revision item.
     *
     * @param {any} commit
     * @param {UgcBuildInterface} build
     *
     * @returns {Promise<void>}
     */
    deleteRevision({commit}: {commit: any}, build: UgcBuildInterface): Promise<void> {
        return api.axios.delete(`api/item/${build.itemId}/build/${build.id}`)
            .then((response: AxiosResponse<void>) => {
                commit('deleteRevisionItem', build);
                return response.data;
            });
    },

    /**
     * Get the pre-signed url.
     *
     * @param {any} commit
     * @param {UgcBuildPresignedRequestInterface} params
     *
     * @returns {Promise<UgcBuildPresignedResponseInterface>}
     */
    presigned(
        {commit}: {commit: any},
        params: UgcBuildPresignedRequestInterface,
    ): Promise<UgcBuildPresignedResponseInterface> {

        return api.axios(`api/item/${params.build.itemId}/build/${params.build.id}/presigned`, {
            method: params.method,
            data: JSON.stringify({size: params.size || 0, extension: params.extension}),
        })
        .then((response: AxiosResponse<UgcBuildPresignedResponseInterface>) => {
                // no need to commit here
                return response.data;
        });
    },

    /**
     * Get the pre-signed url for an image.
     *
     * @param commit
     * @param params
     *
     * @returns {Promise<UgcBuildPresignedResponseInterface>}
     */
    presignedImage(
        {commit}: {commit: any},
        params: UgcBuildPresignedRequestInterface,
    ): Promise<UgcBuildPresignedResponseInterface> {

        return api.axios(`api/item/${params.build.itemId}/build/${params.build.id}/presigned/image`, {
            method: params.method,
            data: JSON.stringify({size: params.size || 0, extension: params.extension}),
        })
        .then((response: AxiosResponse<UgcBuildPresignedResponseInterface>) => {
            // no need to commit here
            return response.data;
        });
    },

    /**
     * Upload the artifact to S3.
     *
     * @param {any} commit
     * @param {UgcBuildPresignedResponseInterface} url
     * @param {File} file
     * @returns {Promise<any>}
     */
    uploadBuildArtifact(
        {commit}: {commit: any},
        {url, file}: {url: UgcBuildPresignedResponseInterface, file: File},
    ): Promise<any> {
        return api.axios.put(url.presigned_url, file, {
            params: {
                skipAuthorization: true,
            },
            headers: {
                'Content-Type': file.type,
            },
        });
    },

    /**
     * Get the pre-signed url.
     *
     * @param {any} commit
     * @param {UgcRevisionInterface} build
     *
     * @returns {Promise<UgcBuildErrorCodeInteface[]>}
     */
    errors({commit}: {commit: any}, build: UgcRevisionInterface): Promise<UgcBuildErrorCodeInteface[]> {
        return api.axios.get(`api/item/${build.itemId}/build/${build.id}/errorcode`)
            .then((response: AxiosResponse<any>) => {
                // no need to commit here
                return response.data.error_codes;
            });
    },
};
